import api from 'store/api';

export interface ParseCodesResponse {
  rows: {
    organisationCreationCode: string;
    orgOwnerEmail?: string;
    licenceCode: string;
    startDate: string;
  }[];
  errors: { row: number; message: string }[];
}

export interface ImportCodesResponse {
  createdOrganisationCreationCodes: number;
  skippedOrganisationCreationCodes: number;
  createdLicenceCodes: number;
  updatedLicenceCodes: number;
  skippedLicenceCodes: number;
  errors: { row: number; message: string }[];
}

export interface OrganisationCreationCodeResponse {
  id: string;
  code: string;
  orgOwnerEmail?: string;
  status: 'available' | 'redeemed';
  redeemedByOrgId?: string;
  redeemedByOrgName?: string;
  redeemedAt?: string;
  createdAt: string;
}

export interface LicenceCodeResponse {
  id: string;
  code: string;
  organisationCreationCode: string;
  startDate?: string;
  expiresAt?: string;
  isRedeemed: boolean;
  redeemedByOrgId?: string;
  redeemedByOrgName?: string;
  redeemedAt?: string;
  createdAt: string;
}

export interface PartnerProductResponse {
  id: string;
  name: string;
  requiresPartnerCode: boolean;
  requiresLicenceCode: boolean;
}

export interface ValidateOrganisationCreationCodeResponse {
  message: string;
  partnerProduct: PartnerProductResponse;
}

export const partnerProductCodesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    validateOrganisationCreationCode: builder.mutation<
      ValidateOrganisationCreationCodeResponse,
      { code: string }
    >({
      query: (body) => ({
        url: 'validate-organisation-creation-code',
        method: 'POST',
        body,
      }),
    }),
    listPartnerProducts: builder.query<PartnerProductResponse[], void>({
      query: () => 'partner-products',
      providesTags: ['PartnerProductCodes'],
    }),
    parseCodes: builder.mutation<
      ParseCodesResponse,
      { productId: string; file: File }
    >({
      query: ({ productId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `partner-product/${productId}/parse-codes`,
          method: 'POST',
          body: formData,
        };
      },
    }),
    importCodes: builder.mutation<
      ImportCodesResponse,
      { productId: string; file: File }
    >({
      query: ({ productId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `partner-product/${productId}/import-codes`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['PartnerProductCodes'],
    }),
    listOrganisationCreationCodes: builder.query<
      OrganisationCreationCodeResponse[],
      { productId: string; status?: string }
    >({
      query: ({ productId, status }) => ({
        url: `partner-product/${productId}/organisation-creation-codes`,
        params: status ? { status } : undefined,
      }),
      providesTags: ['PartnerProductCodes'],
    }),
    listLicenceCodes: builder.query<
      LicenceCodeResponse[],
      { productId: string; status?: string }
    >({
      query: ({ productId, status }) => ({
        url: `partner-product/${productId}/licence-codes`,
        params: status ? { status } : undefined,
      }),
      providesTags: ['PartnerProductCodes'],
    }),
    listOrganisationLicenceCodes: builder.query<
      {
        id: string;
        code: string;
        startDate?: string;
        productId: string;
        productName: string;
        isAllocated: boolean;
      }[],
      { orgId: string; available?: boolean }
    >({
      query: ({ orgId, available }) => ({
        url: `organisation/${orgId}/licence-codes`,
        params: available ? { available: true } : undefined,
      }),
      providesTags: ['PartnerProductCodes'],
    }),
    listAllPartnerProducts: builder.query<PartnerProductResponse[], void>({
      query: () => 'admin/partner-products',
      providesTags: ['PartnerProductCodes'],
    }),
    updatePartnerProduct: builder.mutation<
      PartnerProductResponse,
      {
        productId: string;
        requiresPartnerCode: boolean;
        requiresLicenceCode: boolean;
      }
    >({
      query: ({ productId, ...body }) => ({
        url: `admin/partner-product/${productId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['PartnerProductCodes'],
    }),
  }),
});

export const {
  useListPartnerProductsQuery,
  useParseCodesMutation,
  useImportCodesMutation,
  useListOrganisationCreationCodesQuery,
  useListLicenceCodesQuery,
  useValidateOrganisationCreationCodeMutation,
  useListOrganisationLicenceCodesQuery,
  useListAllPartnerProductsQuery,
  useUpdatePartnerProductMutation,
} = partnerProductCodesApi;
